import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import Flag from "../Flag";
import { useLocale } from "../../i18n/LocaleContext";
import { PERSONAS, PERSONA_KEYS } from "../../i18n/personas";
import { useAuth } from "../../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

export default function Header() {
  const { t, persona, personaKey, setPersonaKey } = useLocale();
  const { user, logout } = useAuth();
  const NAV_LINKS = [
    { label: t("landing.nav.solutions"),  href: "#solution" },
    { label: t("landing.nav.platform"),   href: "#platform" },
    { label: t("landing.nav.categories"), href: "#categories" },
    { label: t("landing.nav.faq"),        href: "#faq" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-20 flex items-center gap-4">
        {/* Logo / Wordmark */}
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group shrink-0 mr-2"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg"
            alt="Infosys"
            className="h-7 md:h-8 w-auto"
          />
          <span className="hidden md:inline-block h-7 w-px bg-slate-300" />
          <img
            src="/unibuy-wordmark.png"
            alt="UniBuy™ by Infosys · Intelligent Sourcing and Procurement Solution"
            className="h-8 md:h-10 w-auto max-w-[132px] sm:max-w-[170px] md:max-w-none object-contain object-left"
            data-testid="header-onesource-logo"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6 mx-auto" data-testid="primary-nav">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => {
                e.preventDefault();
                scrollTo(l.href);
              }}
              className="text-sm text-slate-700 hover:text-[#0072CE] transition-colors font-medium whitespace-nowrap"
              data-testid={`nav-link-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0 ml-auto lg:ml-0">
          {/* Demo Persona switcher — drives locale globally */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="landing-persona-switcher-trigger"
                className="hidden md:inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1.5 rounded-md border border-slate-300 hover:border-[#0072CE] text-slate-700 whitespace-nowrap"
                title="Switch demo persona / language"
              >
                <Flag code={persona.country_code} height={12} />
                <span className="text-xs font-semibold text-slate-900">{persona.label || persona.currency}</span>
                <ChevronDown size={12} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72" data-testid="landing-persona-switcher-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">
                {t("header.persona_menu_label")}
              </DropdownMenuLabel>
              {PERSONA_KEYS.map((k) => {
                const opt = PERSONAS[k];
                const active = k === personaKey;
                const localeLabel = opt.locale === "en" ? "English"
                                  : opt.locale === "nl" ? "Nederlands"
                                  : opt.locale === "pt-BR" ? "Português"
                                  : opt.locale === "zh-CN" ? "简体中文" : opt.locale;
                return (
                  <DropdownMenuItem
                    key={k}
                    data-testid={`landing-persona-option-${k}`}
                    onClick={() => setPersonaKey(k)}
                    className={`gap-3 ${active ? "bg-[#0072CE]/5" : ""}`}
                  >
                    <Flag code={opt.country_code} height={18} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{opt.label || opt.display_name}</div>
                      <div className="text-[11px] text-slate-500">
                        {opt.display_name} · {opt.currency} · {localeLabel}
                      </div>
                    </div>
                    {active && (<span className="text-[10px] font-bold uppercase tracking-wider text-[#0072CE]">Active</span>)}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled className="text-[11px] text-slate-500 leading-snug">
                {t("header.persona_menu_help")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/spec"
            data-testid="header-spec-link"
            title="Product Requirement Document"
            className="hidden xl:inline-flex text-sm text-slate-700 hover:text-[#0072CE] font-medium px-2 whitespace-nowrap"
          >
            PRD
          </Link>
          <Link
            to="/infoshop/admin/enrichment?demo=1"
            data-testid="header-open-enrichment"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3.5 py-2 whitespace-nowrap"
          >
            {t("landing.nav.ai_enrichment")}
          </Link>
          <Link
            to="/infoshop"
            data-testid="header-open-infoshop"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3.5 py-2 whitespace-nowrap"
          >
            {t("landing.nav.open_infoshop")}
          </Link>
          <a
            href="#propay"
            onClick={(e) => { e.preventDefault(); scrollTo("#propay"); }}
            data-testid="header-cta-tail-of-tail"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3.5 py-2 whitespace-nowrap"
          >
            {t("landing.nav.tail_of_tail")}
          </a>
          {user && (
            <button
              type="button"
              onClick={logout}
              data-testid="header-logout-btn"
              title={`Signed in as ${user.display_name} · Sign out`}
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-rose-600 px-2 py-2 whitespace-nowrap"
            >
              <LogOut size={13} />
              <span className="hidden lg:inline">Sign out</span>
            </button>
          )}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="lg:hidden border-t border-slate-200 bg-white"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(l.href);
                }}
                className="py-2 text-slate-800 hover:text-[#0072CE] font-medium"
                data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <div className="h-px bg-slate-200 my-2" />
            <Link
              to="/infoshop"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-link-infoshop"
              className="py-2 text-slate-800 hover:text-[#0072CE] font-medium"
            >
              {t("landing.nav.open_infoshop_arrow")}
            </Link>
            <Link
              to="/infoshop/admin/enrichment?demo=1"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-link-enrichment"
              className="py-2 text-slate-800 hover:text-[#0072CE] font-medium"
            >
              {t("landing.nav.ai_enrichment_arrow")}
            </Link>
            <Link
              to="/spec"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-link-spec"
              className="py-2 text-slate-800 hover:text-[#0072CE] font-medium"
            >
              {t("landing.nav.prd_arrow")}
            </Link>
            <a
              href="#propay"
              onClick={(e) => { e.preventDefault(); scrollTo("#propay"); }}
              data-testid="mobile-cta-tail-of-tail"
              className="mt-3 inline-flex items-center justify-center text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3.5 py-2"
            >
              {t("landing.nav.tail_of_tail")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
